import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { US_STATES } from "@/lib/usStates";
import { toast } from "sonner";
import { Loader2, Clock, CheckCircle2, XCircle } from "lucide-react";
import { CertificateFiles } from "@/components/certificates/CertificateFiles";

const STATUS_META = {
  PENDING_REVIEW: { label: "Pending review", icon: Clock, cls: "badge-reserved" },
  APPROVED: { label: "Approved", icon: CheckCircle2, cls: "badge-available" },
  REJECTED: { label: "Needs attention", icon: XCircle, cls: "badge-reserved" },
  EXPIRED: { label: "Expired", icon: XCircle, cls: "badge-reserved" },
};

export default function Certificate() {
  const [params] = useSearchParams();
  const welcome = params.get("welcome") === "1";
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/dealer/certificate/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/dealer/certificate", form);
      setStatus(r.data);
      window.dispatchEvent(new Event("certificate-status-changed"));
      toast.success("Certificate received — Zelligio will review it shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const meta = status?.status ? STATUS_META[status.status] : null;
  const StatusIcon = meta?.icon;

  return (
    <div className="max-w-2xl" data-testid="dealer-certificate-page">
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Onboarding</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-charcoal">Resale certificate</h1>

      {welcome && (
        <div className="mt-6 rounded-md border border-terracotta/30 bg-[#BC6955]/5 p-5" data-testid="certificate-welcome">
          <p className="text-sm font-medium text-charcoal">Welcome to Zelligio Connect — one more step.</p>
          <p className="mt-1 text-sm text-warmgrey">
            Upload your resale certificate to enable orders, reservations, and samples. You can skip this for
            now and keep browsing — we'll remind you until it's approved.
          </p>
          <Link to="/dealer" data-testid="certificate-skip-link" className="mt-3 inline-block text-sm font-medium text-terracotta underline">
            Skip for now — take me to my dashboard
          </Link>
        </div>
      )}

      <div className="mt-8 rounded-lg border border-border bg-white p-6">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-warmgrey">Status</h2>
          {meta && (
            <span className={`badge-status ${meta.cls} inline-flex items-center gap-1.5`} data-testid="certificate-status-badge">
              <StatusIcon className="h-3.5 w-3.5" /> {meta.label}
            </span>
          )}
        </div>

        {!status?.uploaded && (
          <p className="mt-4 text-sm text-warmgrey" data-testid="certificate-empty-state">
            You may purchase without a resale certificate. Applicable sales tax will be charged.
            Upload a resale certificate for review if you qualify for resale exemption.
          </p>
        )}

        {status?.status === "PENDING_REVIEW" && (
          <div className="mt-4" data-testid="certificate-pending-note">
            <p className="text-sm text-warmgrey">
              Certificate received{status.certificate?.original_filename ? <> — <span className="font-medium text-charcoal">{status.certificate.original_filename}</span></> : ""} —
              pending Zelligio review.
              {(status.certificate?.created_at || status.certificate?.uploaded_at) && (
                <span className="mt-1 block text-xs text-muted-foreground" data-testid="certificate-uploaded-at">
                  Uploaded {new Date(status.certificate.created_at || status.certificate.uploaded_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              )}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              You may continue purchasing; applicable sales tax will be charged until your resale exemption is approved.
            </p>
          </div>
        )}

        {status?.status === "APPROVED" && (
          <p className="mt-4 text-sm text-warmgrey" data-testid="certificate-approved-note">
            Resale certificate approved — qualifying purchases to matching destinations receive resale
            tax-exempt treatment.
            {status.expiration_date && (
              <span data-testid="certificate-expiration-line">
                {" "}Expires <span className="font-medium text-charcoal num">{status.expiration_date}</span>
                {status.days_remaining != null && ` (${status.days_remaining} day${status.days_remaining === 1 ? "" : "s"} remaining)`}.
              </span>
            )}
          </p>
        )}

        {status?.status === "EXPIRED" && (
          <p className="mt-4 text-sm text-warmgrey" data-testid="certificate-expired-note">
            Your resale certificate has expired. You may continue purchasing with applicable sales tax
            until a current replacement is approved.
          </p>
        )}

        {status?.uploaded && (status?.uncovered_states || []).length > 0 && (status?.pending_states || []).length === 0 && (
          <p className="mt-4 text-sm text-warmgrey" data-testid="certificate-uncovered-note">
            No current certificate on file for{" "}
            {status.uncovered_states.map((s) => US_STATES[s] || s).join(", ")} — purchases shipped there
            will be charged applicable sales tax.
          </p>
        )}

        <CertificateFiles refreshKey={status?.certificate?.id} />

        {(status?.jurisdictions || []).length > 0 && (
          <div className="mt-5 border-t border-border pt-5" data-testid="certificate-jurisdictions">
            <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Certificates by jurisdiction</p>
            <ul className="mt-2 space-y-2">
              {status.jurisdictions.map((j) => {
                const jm = STATUS_META[j.status];
                return (
                  <li key={j.state} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`certificate-jurisdiction-${j.state}`}>
                    <span className="w-32 font-medium text-charcoal">{US_STATES[j.state] || j.state}</span>
                    {jm && <span className={`badge-status ${jm.cls}`}>{jm.label}</span>}
                    {j.current && <span className="badge-status badge-available">current</span>}
                    {j.expiration_date && (
                      <span className="text-xs text-warmgrey num">
                        exp {j.expiration_date}{j.days_remaining != null && ` · ${j.days_remaining}d`}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {status?.pending_certificate && status?.current_certificate && (
          <p className="mt-4 rounded-md border border-border bg-sand p-3 text-sm text-warmgrey" data-testid="certificate-replacement-pending">
            Your replacement certificate is under review — your current certificate stays active
            {status.expiration_date ? ` until ${status.expiration_date}` : ""}.
          </p>
        )}

        {status?.status === "REJECTED" && (
          <div className="mt-4" data-testid="certificate-rejected-note">
            <p className="text-sm text-warmgrey">Your resale certificate was not approved. You may continue purchasing with applicable sales tax, or upload corrected documentation.</p>
            {status.review_note && (
              <p className="mt-2 rounded-md border border-status-reserved/40 bg-[#D4A373]/10 p-3 text-sm text-[#8a6435]" data-testid="certificate-review-note">
                {status.review_note}
              </p>
            )}
            <p className="mt-2 text-sm text-warmgrey">Please upload a clearer or updated document below.</p>
          </div>
        )}

        {(!status?.uploaded || status?.status === "REJECTED" || status?.status === "EXPIRED" || (status?.status === "PENDING_REVIEW" && replaceMode)) && (
          <div className="mt-6 border-t border-border pt-6">
            <label htmlFor="certificate-file-input" className="text-sm font-medium text-charcoal">
              {status?.status === "PENDING_REVIEW" ? "Replace pending certificate" : status?.uploaded ? "Upload a new version" : "Upload certificate"}
            </label>
            {status?.status === "PENDING_REVIEW" && (
              <p className="mt-1 text-xs font-medium text-status-reserved" data-testid="certificate-replace-warning">
                Replacing restarts the review — your previous submission stays on record.
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">PDF or image (JPG, PNG, WebP), up to 10MB.</p>
            <div className="mt-3 flex items-center gap-3">
              <input
                id="certificate-file-input"
                ref={inputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleUpload}
                disabled={uploading}
                className="block w-full text-sm text-warmgrey file:mr-4 file:rounded-md file:border-0 file:bg-sand file:px-4 file:py-2 file:text-sm file:font-medium file:text-charcoal hover:file:bg-[#EAE8E3]"
                data-testid="certificate-file-input"
              />
              {uploading && <Loader2 className="h-4 w-4 animate-spin text-terracotta" data-testid="certificate-uploading" />}
            </div>
          </div>
        )}
        {status?.status === "PENDING_REVIEW" && !replaceMode && (
          <div className="mt-6 border-t border-border pt-6">
            <button type="button" onClick={() => setReplaceMode(true)} data-testid="certificate-replace-button"
              className="text-sm font-medium text-terracotta underline underline-offset-2 hover:text-terracotta-hover">
              Replace pending certificate
            </button>
            <p className="mt-1 text-xs text-muted-foreground">Only if you uploaded the wrong document — replacement restarts the review.</p>
          </div>
        )}
      </div>
    </div>
  );
}
